import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { almacen, descargar, newOperationId, type Supply } from '../api';
import { Boton, Cargando, Fallo, Filtro, Pastilla, Rotulo } from '../componentes';
import { descargarCsv } from '../csv';
import { NIVEL, cantidad, diaDeLima, soles, sumarDias } from '../formato';
import { IGV_COMPRAS, costoPorUnidadDeStock } from '../numeros';
import {
  deLaTemporada,
  temporadasDe,
  aMilesimas,
  deCentimos,
  deMilesimas,
  entradasDe,
  etiquetasDe,
  filasDeVentasCsv,
  matrizDe,
  ventasDe,
  type Matriz,
  type MatrizDeVentas,
  type TotalDeVenta,
  type VariantFamily,
  pendientesDe,
} from '../prendas';
import { useVocabulario } from '../vocabulario';
import { RecepcionPorSerie } from './RecepcionPorSerie';
import { FiltroDeTemporada } from '../Temporada';

/**
 * El stock de las prendas en matriz de tallas por colores, y recibir por
 * casilla. Docs/34 §10.
 *
 * Una tienda no piensa «Polo básico · M · Negro: 4». Piensa el polo entero:
 * cuántas M quedan de todos los colores, qué color se acabó. Y cuando llega
 * la caja del proveedor trae una lista así, talla por color. Recibirla insumo
 * por insumo eran doce formularios para una sola caja.
 */
export function PorPrenda({ familias, insumos }: { familias: VariantFamily[]; insumos: Supply[] }) {
  const vocabulario = useVocabulario();
  const [periodo, setPeriodo] = useState<Periodo>('7');
  const { desde, hasta } = limitesDe(periodo);

  /*
   * Lo vendido de cada talla, para verlo al lado de lo que queda: la tienda
   * repone mirando las dos cifras juntas. Neto de devoluciones, del día de
   * Lima. Docs/15 §2.50.
   */
  const ventas = useQuery({
    queryKey: ['prendas-ventas', desde, hasta],
    queryFn: () => almacen.variantFamilySales(desde, hasta),
  });

  // Lo apartado en el probador: la caja ya no lo ofrece libre, y el almacén
  // tiene que decir lo mismo de la última M. Sin red o con un servidor de
  // antes, la cuadrícula sigue sin apartados.
  const apartados = useQuery({
    queryKey: ['apartados'],
    queryFn: almacen.heldUnits,
    retry: false,
  });
  const apartadasPorProducto = new Map(
    (apartados.data?.heldUnits ?? []).map((fila) => [fila.productId, aMilesimas(fila.quantity)]),
  );

  const [temporada, setTemporada] = useState<string | null>(null);
  const matrices = deLaTemporada(familias, temporada).map((familia) => {
    const matriz = matrizDe(familia, insumos, apartadasPorProducto);
    return {
      familia,
      matriz,
      ventas: ventas.data === undefined ? null : ventasDe(matriz, ventas.data),
    };
  });

  function exportar() {
    const conVentas = matrices.flatMap((cada) =>
      cada.ventas === null ? [] : [{ familia: cada.familia, ventas: cada.ventas }],
    );
    const { cabecera, filas } = filasDeVentasCsv(conVentas, vocabulario);
    descargarCsv(
      `ventas-por-${vocabulario.familiaColumna.toLowerCase()}-${desde}-a-${hasta}.csv`,
      cabecera,
      filas,
    );
  }

  return (
    <div className="prendas">
      <div className="filtros">
        <span className="apagado">Vendido en</span>
        {PERIODOS.map((opcion) => (
          <Filtro
            key={opcion.id}
            texto={opcion.texto}
            activo={periodo === opcion.id}
            onClick={() => setPeriodo(opcion.id)}
          />
        ))}
        <span className="separador" />
        <Boton onClick={exportar} disabled={ventas.data === undefined}>
          Exportar CSV
        </Boton>
      </div>

      <FiltroDeTemporada temporadas={temporadasDe(familias)} elegida={temporada} alElegir={setTemporada} />

      {ventas.isError ? <Fallo error={ventas.error} onReintentar={() => ventas.refetch()} /> : null}

      {matrices.map((cada) => (
        <Prenda key={cada.familia.id} familia={cada.familia} matriz={cada.matriz} ventas={cada.ventas} />
      ))}
    </div>
  );
}

type Periodo = '7' | '30' | 'mes';

const PERIODOS: { id: Periodo; texto: string }[] = [
  { id: '7', texto: 'Últimos 7 días' },
  { id: '30', texto: 'Últimos 30 días' },
  { id: 'mes', texto: 'Este mes' },
];

/** Los dos días de Lima del periodo, los dos incluidos. */
function limitesDe(periodo: Periodo): { desde: string; hasta: string } {
  const hoy = diaDeLima();
  if (periodo === 'mes') return { desde: `${hoy.slice(0, 7)}-01`, hasta: hoy };
  return { desde: sumarDias(hoy, -(Number(periodo) - 1)), hasta: hoy };
}

type Vista = 'stock' | 'ventas' | 'recibir';

function Prenda({
  familia,
  matriz,
  ventas,
}: {
  familia: VariantFamily;
  matriz: Matriz;
  ventas: MatrizDeVentas | null;
}) {
  const [vista, setVista] = useState<Vista>('stock');
  const vocabulario = useVocabulario();
  // Lo que acaba de entrar, para su hoja de etiquetas: el almacén web no llega
  // a la térmica del POS, y la prenda sin etiqueta no se puede colgar.
  const [recienLlegadas, setRecienLlegadas] = useState<{ productId: string; copies: number }[]>([]);
  const [imprimiendo, setImprimiendo] = useState(false);
  const [falloAlImprimir, setFalloAlImprimir] = useState<unknown>(null);

  async function imprimirEtiquetas() {
    setImprimiendo(true);
    setFalloAlImprimir(null);
    try {
      await descargar('/catalog/labels/pdf', `etiquetas-${familia.sku}-${diaDeLima()}.pdf`, {
        items: recienLlegadas,
      });
    } catch (fallo) {
      setFalloAlImprimir(fallo);
    } finally {
      setImprimiendo(false);
    }
  }

  return (
    <section className="tarjeta prenda">
      <div className="prenda__cabeza">
        <div>
          <h3 className="tarjeta__titulo">{familia.name}</h3>
          <span className="mono apagado">
            {familia.sku} · {cantidad(deMilesimas(matriz.total))} {matriz.unidad} en total
            {ventas === null ? null : ` · ${cantidad(deMilesimas(ventas.total.vendidas))} vendidas`}
          </span>
          {matriz.vendidasDeMas > 0 ? (
            /*
             * Aparte y no restando: el total ya cuenta cero esas tallas, como
             * la caja. Lo que dice aquí es que hay algo por arreglar —registrar
             * lo que llegó o contar—, no que falta comprar.
             */
            <span className="negativo">
              {' '}
              ·{' '}
              {matriz.vendidasDeMas === 1
                ? `1 ${vocabulario.variante} vendida de más`
                : `${matriz.vendidasDeMas} ${vocabulario.variantes} vendidas de más`}
            </span>
          ) : null}
        </div>
        <div className="filtros">
          <Filtro texto="Stock" activo={vista === 'stock'} onClick={() => setVista('stock')} />
          <Filtro texto="Ventas" activo={vista === 'ventas'} onClick={() => setVista('ventas')} />
          <Boton onClick={() => setVista(vista === 'recibir' ? 'stock' : 'recibir')}>
            {vista === 'recibir' ? 'Ver el stock' : `Recibir por ${vocabulario.variante}`}
          </Boton>
        </div>
      </div>

      {vista !== 'recibir' && recienLlegadas.length > 0 ? (
        <div className="filtros">
          <span>
            Entraron {recienLlegadas.reduce((suma, una) => suma + una.copies, 0)}: una etiqueta por {vocabulario.pieza}, en hoja A4 de 24 que
            sale por cualquier impresora.
          </span>
          <Boton variante="primario" onClick={() => void imprimirEtiquetas()} disabled={imprimiendo}>
            {imprimiendo ? 'Preparando…' : 'Imprimir estas etiquetas'}
          </Boton>
          <button type="button" className="enlace-sutil" onClick={() => setRecienLlegadas([])}>
            Ya no
          </button>
          {falloAlImprimir === null ? null : <Fallo error={falloAlImprimir} />}
        </div>
      ) : null}

      {vista === 'recibir' ? (
        <Recibir
          matriz={matriz}
          alTerminar={(recibidas) => {
            setRecienLlegadas(etiquetasDe(recibidas));
            setVista('stock');
          }}
        />
      ) : vista === 'ventas' ? (
        ventas === null ? (
          <Cargando que="las ventas" />
        ) : (
          <TablaDeVentas ventas={ventas} unidad={matriz.unidad} />
        )
      ) : (
        <Tabla matriz={matriz} />
      )}
    </section>
  );
}

/**
 * Lo vendido de cada talla, con lo que queda debajo. Las filas y columnas
 * suman unidades y, con el permiso de reportes, el importe.
 */
function TablaDeVentas({ ventas, unidad }: { ventas: MatrizDeVentas; unidad: string }) {
  const total = (cifra: TotalDeVenta) => (
    <span className="prenda__celda">
      <span className="mono">{cantidad(deMilesimas(cifra.vendidas))}</span>
      {cifra.centimos === null ? null : (
        <span className="mono apagado">{soles(deCentimos(cifra.centimos))}</span>
      )}
    </span>
  );

  return (
    <div className="tabla-envoltura prenda__matriz">
      <table>
        <thead>
          <tr>
            <th>{ventas.ejeFilas}</th>
            {ventas.columnas.map((columna) => (
              <th key={columna} className="numero">
                {ventas.ejeColumnas === null ? 'Vendidas' : columna}
              </th>
            ))}
            {ventas.ejeColumnas === null ? null : <th className="numero">Total</th>}
          </tr>
        </thead>
        <tbody>
          {ventas.filas.map((fila, indiceFila) => (
            <tr key={fila}>
              <td>
                <strong>{fila}</strong>
              </td>
              {ventas.casillas[indiceFila]!.map((casilla, indiceColumna) => (
                <td key={ventas.columnas[indiceColumna]} className="numero">
                  {casilla === null ? (
                    <span className="apagado">—</span>
                  ) : (
                    <span className="prenda__celda">
                      <strong className="mono">{cantidad(deMilesimas(casilla.vendidas))}</strong>
                      <span
                        className="mono apagado"
                        title={casilla.devueltas > 0 ? `Volvieron ${cantidad(deMilesimas(casilla.devueltas))}` : undefined}
                      >
                        {casilla.unida ? `quedan ${cantidad(deMilesimas(casilla.stock))}` : 'sin unir'}
                      </span>
                    </span>
                  )}
                </td>
              ))}
              {ventas.ejeColumnas === null ? null : (
                <td className="numero">{total(ventas.totalPorFila[indiceFila]!)}</td>
              )}
            </tr>
          ))}
          <tr>
            <td className="apagado">Total ({unidad})</td>
            {ventas.totalPorColumna.map((cifra, indice) => (
              <td key={ventas.columnas[indice]} className="numero">
                {total(cifra)}
              </td>
            ))}
            {ventas.ejeColumnas === null ? null : <td className="numero">{total(ventas.total)}</td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Tallas en filas, colores en columnas, con lo que suma cada una. */
function Tabla({ matriz }: { matriz: Matriz }) {
  const vocabulario = useVocabulario();
  return (
    <div className="tabla-envoltura prenda__matriz">
      <table>
        <thead>
          <tr>
            <th>{matriz.ejeFilas}</th>
            {matriz.columnas.map((columna) => (
              <th key={columna} className="numero">
                {matriz.ejeColumnas === null ? 'Stock' : columna}
              </th>
            ))}
            {matriz.ejeColumnas === null ? null : <th className="numero">Total</th>}
          </tr>
        </thead>
        <tbody>
          {matriz.filas.map((fila, indiceFila) => (
            <tr key={fila}>
              <td>
                <strong>{fila}</strong>
              </td>
              {matriz.casillas[indiceFila]!.map((casilla, indiceColumna) => (
                <td key={matriz.columnas[indiceColumna]} className="numero">
                  {casilla === null ? (
                    <span className="apagado">—</span>
                  ) : casilla.supplyId === null ? (
                    // Existe y se vende, pero sin insumo no hay estante que contar.
                    <span className="apagado" title={`Esta ${vocabulario.variante} no está unida al almacén`}>
                      sin unir
                    </span>
                  ) : (
                    <span className="prenda__celda">
                      <span className="mono">{cantidad(deMilesimas(casilla.milesimas))}</span>
                      {casilla.nivel === null || casilla.nivel === 'ok' ? null : (
                        <Pastilla texto={NIVEL[casilla.nivel].texto} tono={NIVEL[casilla.nivel].tono} />
                      )}
                      {casilla.apartadas > 0 ? (
                        <span className="mono apagado">
                          {cantidad(deMilesimas(casilla.apartadas))}{' '}
                          {casilla.apartadas === 1000 ? 'apartada' : 'apartadas'}
                        </span>
                      ) : null}
                    </span>
                  )}
                </td>
              ))}
              {matriz.ejeColumnas === null ? null : (
                <td className="numero mono">
                  {cantidad(deMilesimas(matriz.totalPorFila[indiceFila] ?? 0))}
                </td>
              )}
            </tr>
          ))}
          {matriz.ejeColumnas === null ? null : (
            <tr>
              <td className="apagado">Total</td>
              {matriz.totalPorColumna.map((total, indice) => (
                <td key={matriz.columnas[indice]} className="numero mono">
                  {cantidad(deMilesimas(total))}
                </td>
              ))}
              <td className="numero mono">
                <strong>{cantidad(deMilesimas(matriz.total))}</strong>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/**
 * La caja del proveedor, casilla por casilla: cuántas de cada talla y un
 * solo precio por prenda. Cada casilla es una entrada al insumo de su talla,
 * con su propia operación: si la red se cae a la mitad, volver a confirmar
 * reintenta las mismas y no duplica las que ya entraron.
 */
function Recibir({
  matriz,
  alTerminar,
}: {
  matriz: Matriz;
  alTerminar: (recibidas: { productId: string; cantidad: string }[]) => void;
}) {
  const clientes = useQueryClient();
  const vocabulario = useVocabulario();
  const [tecleado, setTecleado] = useState<Record<string, string>>({});
  const [costo, setCosto] = useState('');
  const [conIgv, setConIgv] = useState(false);
  const [guia, setGuia] = useState('');
  const [ordenId, setOrdenId] = useState('');
  /*
   * Quién lo trajo, cuando llega sin orden: el taller de Gamarra que cosió el
   * bulto, no el proveedor habitual de la ficha. Sin esto no había dónde
   * decirlo, y la trazabilidad del lote apuntaba a otro (8 oct).
   */
  const [proveedorId, setProveedorId] = useState('');
  const proveedores = useQuery({ queryKey: ['proveedores'], queryFn: almacen.suppliers });
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<unknown>(null);

  /*
   * Las casillas que ya entraron en un intento que se cortó a la mitad. Se
   * bloquean y no se vuelven a mandar: si alguien corregía una que ya había
   * entrado —10 por 12— y confirmaba otra vez, la nueva operación sumaba 12
   * encima de los 10 y la talla quedaba en 22.
   */
  const [entraron, setEntraron] = useState<ReadonlySet<string>>(() => new Set());

  /*
   * La caja de Gamarra suele venir de una orden de compra: recibir contra ella
   * la cierra y deja dicho de qué proveedor llegó. La recepción por talla no
   * lo pedía, y la orden se quedaba abierta para siempre.
   */
  const ordenes = useQuery({ queryKey: ['ordenes-de-compra'], queryFn: almacen.purchaseOrders });
  const tallas = new Set(
    matriz.casillas.flat().flatMap((casilla) => (casilla?.supplyId == null ? [] : [casilla.supplyId])),
  );
  const ordenesDeEstaPrenda = (ordenes.data ?? []).filter(
    (orden) =>
      (orden.status === 'sent' || orden.status === 'in_transit' || orden.status === 'partial') &&
      orden.lines.some((linea) => tallas.has(linea.supplyId)),
  );

  // Una operación por casilla, fija mientras no se confirme con éxito.
  const [operaciones] = useState(() => new Map<string, string>());
  const operacionDe = (productId: string) => {
    let id = operaciones.get(productId);
    if (id === undefined) {
      id = newOperationId();
      operaciones.set(productId, id);
    }
    return id;
  };

  /*
   * Lo que va por serie no entra por cantidad: el servidor lo niega
   * (`SERIAL_RECEIPT_REQUIRED`), y «Recibir por versión» de los iPhone fallaba
   * casilla por casilla (8 oct). Esas versiones se reciben abajo, escaneando
   * cada caja; las demás siguen en la matriz.
   */
  const insumos = useQuery({ queryKey: ['insumos'], queryFn: almacen.supplies });
  const fichaDe = (supplyId: string | null) =>
    supplyId === null ? undefined : (insumos.data ?? []).find((insumo) => insumo.id === supplyId);
  const porSerie = (supplyId: string | null) => fichaDe(supplyId)?.isSerialTracked ?? false;
  const versionesPorSerie = matriz.casillas
    .flatMap((fila, indiceFila) =>
      fila.map((casilla, indiceColumna) =>
        casilla !== null && porSerie(casilla.supplyId)
          ? {
              supply: fichaDe(casilla.supplyId)!,
              nombre: [matriz.filas[indiceFila], matriz.ejeColumnas === null ? null : matriz.columnas[indiceColumna]]
                .filter((parte) => parte !== null && parte !== undefined)
                .join(' · '),
            }
          : null,
      ),
    )
    .filter((version): version is { supply: Supply; nombre: string } => version !== null);
  const [porSerieElegida, setPorSerieElegida] = useState<string>('');
  const versionPorSerie =
    versionesPorSerie.find((version) => version.supply.id === porSerieElegida) ?? versionesPorSerie[0];

  const leidas = entradasDe(matriz, tecleado);
  // Lo que ya entró no se vuelve a mandar (`pendientesDe`), y lo que va por
  // serie se recibe abajo, caja por caja.
  const entradas = pendientesDe(leidas.entradas, entraron).filter(
    (entrada) => !porSerie(entrada.supplyId),
  );
  const sinInsumo = leidas.sinInsumo;
  // El costo viaja como texto y se calcula en enteros, como en la recepción
  // de siempre: el dinero no es un `number` (ver `costoPorUnidadDeStock`).
  const costoSinIgv = costoPorUnidadDeStock(costo, { conIgv });
  const unidades = entradas.reduce((suma, entrada) => suma + Number.parseFloat(entrada.cantidad), 0);
  const listo = entradas.length > 0 && costoSinIgv !== null && !enviando;

  async function confirmar() {
    setEnviando(true);
    setError(null);

    try {
      for (const entrada of entradas) {
        await almacen.receive({
          operationId: operacionDe(entrada.productId),
          supplyId: entrada.supplyId,
          quantity: entrada.cantidad,
          unitCost: costoSinIgv!,
          ...(guia.trim() === '' ? {} : { document: guia.trim() }),
          ...(ordenId === '' ? {} : { purchaseOrderId: ordenId }),
          ...(ordenId === '' && proveedorId !== '' ? { supplierId: proveedorId } : {}),
        });
        setEntraron((antes) => new Set([...antes, entrada.productId]));
      }

      void clientes.invalidateQueries();
      alTerminar(entradas);
    } catch (fallo) {
      // Lo que entró ya entró: al reintentar, sus operaciones contestan lo
      // mismo sin sumar otra vez.
      setError(fallo);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="formulario">
      <div className="tabla-envoltura prenda__matriz">
        <table>
          <thead>
            <tr>
              <th>{matriz.ejeFilas}</th>
              {matriz.columnas.map((columna) => (
                <th key={columna}>{matriz.ejeColumnas === null ? 'Llegaron' : columna}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matriz.filas.map((fila, indiceFila) => (
              <tr key={fila}>
                <td>
                  <strong>{fila}</strong>
                </td>
                {matriz.casillas[indiceFila]!.map((casilla, indiceColumna) => (
                  <td key={matriz.columnas[indiceColumna]}>
                    {casilla === null ? (
                      <span className="apagado">—</span>
                    ) : (
                      <input
                        className="prenda__entrada"
                        inputMode="decimal"
                        placeholder="0"
                        aria-label={`${fila} ${matriz.columnas[indiceColumna] ?? ''}`.trim()}
                        disabled={casilla.supplyId === null || porSerie(casilla.supplyId) || entraron.has(casilla.productId)}
                        title={
                          porSerie(casilla.supplyId)
                            ? 'Va por serie: se recibe abajo, caja por caja'
                            : entraron.has(casilla.productId)
                              ? 'Ya entró: no se vuelve a mandar'
                              : undefined
                        }
                        value={porSerie(casilla.supplyId) ? '' : (tecleado[casilla.productId] ?? '')}
                        onChange={(evento) => {
                          // Otra cantidad es otra entrada: la operación de la
                          // anterior contestaría con lo de antes.
                          operaciones.delete(casilla.productId);
                          setTecleado({ ...tecleado, [casilla.productId]: evento.target.value });
                        }}
                      />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="formulario__fila">
        <div className="campo">
          <Rotulo
            htmlFor="prenda-costo"
            ayuda={`Lo que costó cada ${vocabulario.pieza}, igual para todas las ${vocabulario.variantes}. Si el precio trae IGV, márcalo abajo y se lo quita.`}
          >
            Precio por {matriz.unidad}
          </Rotulo>
          <input
            id="prenda-costo"
            inputMode="decimal"
            placeholder="18,50"
            value={costo}
            onChange={(evento) => setCosto(evento.target.value)}
          />
        </div>
        <div className="campo">
          <Rotulo htmlFor="prenda-guia" ayuda="El número de la guía o factura del proveedor, para encontrarla después si algo no cuadra.">
            Guía de remisión
          </Rotulo>
          <input
            id="prenda-guia"
            placeholder="GR-44821"
            value={guia}
            onChange={(evento) => setGuia(evento.target.value)}
          />
        </div>
      </div>

      {ordenesDeEstaPrenda.length > 0 ? (
        <div className="campo">
          <Rotulo
            htmlFor="prenda-orden"
            ayuda="Si esto llegó por una orden de compra, elígela: queda dicho de qué proveedor vino y la orden se da por recibida."
          >
            Contra la orden de compra
          </Rotulo>
          <select
            id="prenda-orden"
            value={ordenId}
            disabled={entraron.size > 0}
            onChange={(evento) => setOrdenId(evento.target.value)}
          >
            <option value="">Sin orden de compra</option>
            {ordenesDeEstaPrenda.map((orden) => (
              <option key={orden.id} value={orden.id}>
                {orden.number} · {orden.supplier}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {ordenId === '' && (proveedores.data ?? []).length > 0 ? (
        <div className="campo">
          <Rotulo
            htmlFor="prenda-proveedor"
            ayuda="Quién trajo este bulto, si llegó sin orden de compra. Si lo dejas en blanco queda el proveedor de la ficha."
          >
            Lo trajo
          </Rotulo>
          <select
            id="prenda-proveedor"
            value={proveedorId}
            disabled={entraron.size > 0}
            onChange={(evento) => setProveedorId(evento.target.value)}
          >
            <option value="">El de la ficha</option>
            {(proveedores.data ?? []).map((proveedor) => (
              <option key={proveedor.id} value={proveedor.id}>
                {proveedor.name}
              </option>
            ))}
          </select>
        </div>
      ) : null}

      {entraron.size > 0 ? (
        <p className="aviso-caja aviso-caja--aviso">
          {entraron.size === 1
            ? `Una ${vocabulario.variante} ya entró`
            : `${entraron.size} ${vocabulario.variantes} ya entraron`}{' '}
          antes de que se cortara: quedan bloqueadas y no se vuelven a mandar.
        </p>
      ) : null}

      <button
        type="button"
        className={`opcion ${conIgv ? 'opcion--activa' : ''}`}
        aria-pressed={conIgv}
        onClick={() => setConIgv(!conIgv)}
      >
        El precio incluye IGV ({IGV_COMPRAS} %)
        <Pastilla texto={conIgv ? 'se le quita al entrar' : 'ya va sin IGV'} tono={conIgv ? 'aviso' : 'neutro'} />
      </button>

      {sinInsumo.length > 0 ? (
        <p className="aviso-caja aviso-caja--aviso">
          {sinInsumo.length === 1
            ? `Una ${vocabulario.variante} no está unida`
            : `${sinInsumo.length} ${vocabulario.variantes} no están unidas`}{' '}
          al
          almacén y no puede recibir. Únelas en Recetas y costeo, o desde la reventa pendiente.
        </p>
      ) : null}

      {error === null ? null : <Fallo error={error} />}

      <Boton variante="primario" onClick={() => void confirmar()} disabled={!listo}>
        {enviando
          ? 'Ingresando…'
          : entradas.length === 0
            ? 'Escribe cuántas llegaron'
            : `Confirmar ${cantidad(String(unidades))} ${matriz.unidad} en ${entradas.length} ${entradas.length === 1 ? vocabulario.variante : vocabulario.variantes}`}
      </Boton>

      {versionPorSerie === undefined ? null : (
        <div className="formulario">
          <p className="aviso-caja">
            {versionesPorSerie.length === 1
              ? 'Esta versión va por serie:'
              : `${versionesPorSerie.length} versiones van por serie:`}{' '}
            cada caja entra con su IMEI o su número, y es lo que la caja venderá y la garantía
            buscará.
          </p>
          {versionesPorSerie.length > 1 ? (
            <div className="campo">
              <Rotulo htmlFor="prenda-por-serie" ayuda="Qué versión vas a escanear ahora.">
                Versión
              </Rotulo>
              <select
                id="prenda-por-serie"
                value={versionPorSerie.supply.id}
                onChange={(evento) => setPorSerieElegida(evento.target.value)}
              >
                {versionesPorSerie.map((version) => (
                  <option key={version.supply.id} value={version.supply.id}>
                    {version.nombre}
                  </option>
                ))}
              </select>
            </div>
          ) : null}
          <RecepcionPorSerie key={versionPorSerie.supply.id} insumo={versionPorSerie.supply} guia={guia} />
        </div>
      )}
    </div>
  );
}
